import { Song } from '@/types/player';

// 🚀 OTIMIZAÇÃO: Cache em memória para máxima velocidade
const imageCache = new Set<string>();
const loadingImages = new Set<string>();

// --- NOVO: Sistema de Pub/Sub para notificar sobre o carregamento do cache ---
const subscribers = new Set<() => void>();
let isCacheLoadedFromDB = false;

/**
 * Assina as atualizações do cache de imagens. O callback é chamado quando o
 * cache inicial do IndexedDB é carregado.
 * @param callback A função a ser chamada quando o cache estiver pronto.
 * @returns Uma função para cancelar a assinatura.
 */
export function subscribeToCacheChanges(callback: () => void): () => void {
  subscribers.add(callback);
  // Se o cache já foi carregado, chama o callback imediatamente.
  if (isCacheLoadedFromDB) {
    callback();
  }
  return () => {
    subscribers.delete(callback);
  };
}

function notifySubscribers() {
  // Notifica todos os componentes assinados.
  for (const callback of subscribers) {
    callback();
  }
}
// --- FIM DO NOVO SISTEMA ---

// 🚀 OTIMIZAÇÃO: Cache em IndexedDB para persistência mais eficiente
const DB_NAME = 'image_cache_db';
const STORE_NAME = 'image_cache';

async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'url' });
      }
    };
  });
}

// 🚀 OTIMIZAÇÃO: Carrega cache do IndexedDB na inicialização
(async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      request.result.forEach((item: { url: string }) => {
        imageCache.add(item.url);
      });
      // ✅ Notifica os assinantes que o cache foi carregado.
      isCacheLoadedFromDB = true;
      notifySubscribers();
    };
    request.onerror = () => {
      // Mesmo em caso de erro, notifica para não bloquear a UI.
      isCacheLoadedFromDB = true;
      notifySubscribers();
    }
  } catch (error) {
    console.warn('Erro ao carregar cache de imagens:', error);
    // Mesmo em caso de erro, notifica para não bloquear a UI.
    isCacheLoadedFromDB = true;
    notifySubscribers();
  }
})();

// 🚀 OTIMIZAÇÃO: Salva URL no IndexedDB
async function saveToIndexedDB(url: string) {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.put({ url });
  } catch (error) {
    console.warn('Erro ao salvar no IndexedDB:', error);
  }
}

// Função para verificar se uma imagem já está em cache
export const isImageCached = (url: string): boolean => {
  return imageCache.has(url) || loadingImages.has(url);
};

// 🚀 OTIMIZAÇÃO: Função ultra-rápida para pré-carregar uma única imagem
export const preloadImage = (url: string): Promise<void> => {
  if (!url || imageCache.has(url)) return Promise.resolve();
  if (loadingImages.has(url)) return Promise.resolve();

  loadingImages.add(url);
  
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      loadingImages.delete(url);
      imageCache.add(url);
      saveToIndexedDB(url);
      resolve();
    };
    
    img.onerror = () => {
      loadingImages.delete(url);
      resolve();
    };

    // 🚀 OTIMIZAÇÃO: Atributos para melhor performance no Android WebView
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  });
};

// 🚀 OTIMIZAÇÃO: Função para pré-carregar múltiplas imagens em paralelo com limite
export const preloadImages = async (urls: string[]): Promise<void[]> => {
  const validUrls = urls.filter(url => !isImageCached(url));
  if (validUrls.length === 0) return Promise.resolve([]);

  // Limita o número de carregamentos simultâneos para evitar sobrecarga
  const BATCH_SIZE = 5;
  const results: Promise<void>[] = [];

  for (let i = 0; i < validUrls.length; i += BATCH_SIZE) {
    const batch = validUrls.slice(i, i + BATCH_SIZE);
    const batchPromises = batch.map(preloadImage);
    results.push(...batchPromises);
    
    // Aguarda o lote atual terminar antes de iniciar o próximo
    await Promise.all(batchPromises);
  }

  return Promise.all(results);
};

// 🚀 OTIMIZAÇÃO: Função para pré-carregar imagens em background com prioridade
export const preloadImagesInBackground = (urls: string[]): void => {
  const validUrls = urls.filter(url => !isImageCached(url));
  if (validUrls.length === 0) return;

  // Divide as URLs em alta e baixa prioridade
  const highPriorityUrls = validUrls.slice(0, 5); // Primeiras 5 são alta prioridade
  const lowPriorityUrls = validUrls.slice(5);

  // Carrega imagens de alta prioridade imediatamente
  highPriorityUrls.forEach(url => {
    preloadImage(url).catch(() => {});
  });

  // Carrega imagens de baixa prioridade com delay
  if (lowPriorityUrls.length > 0) {
    setTimeout(() => {
      preloadImages(lowPriorityUrls).catch(() => {});
    }, 1000);
  }
};

// 🚀 OTIMIZAÇÃO: Função para pré-carregar imagens de músicas com prioridade
export const preloadSongImages = async (songs: Song[]): Promise<void[]> => {
  const urls = songs
    .map(song => song.capa_url)
    .filter((url): url is string => !!url && !isImageCached(url));

  // Carrega as primeiras 5 imagens com alta prioridade
  const highPriorityUrls = urls.slice(0, 5);
  const lowPriorityUrls = urls.slice(5);

  // Carrega imagens de alta prioridade primeiro
  await preloadImages(highPriorityUrls);

  // Depois carrega as de baixa prioridade
  return preloadImages(lowPriorityUrls);
};

// Limpa o cache
export const clearImageCache = async () => {
  imageCache.clear();
  loadingImages.clear();

  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.clear();
  } catch (error) {
    console.warn('Erro ao limpar cache de imagens:', error);
  }
}; 